"use client";

import { Link, useNavigate } from "react-router-dom";
import { Users, UserPlus, UserCheck } from "lucide-react";
import { useState, useEffect } from "react";
import { successToast,errorToast } from '../../utils/toasters'

function GroupCard({ group, compact = false, onJoin, onLeave, isProcessing }) {
  const [localJoinedStatus, setLocalJoinedStatus] = useState(group.isJoined);
  const [localMemberCount, setLocalMemberCount] = useState(
    Number.parseInt(group.member_count || 0, 10)
  );
  const [localProcessing, setLocalProcessing] = useState(false);
  const navigate = useNavigate();

  // Check if user is logged in
  const isLoggedIn = localStorage.getItem("access_token") !== null;

  useEffect(() => {
    setLocalJoinedStatus(group.isJoined);
    setLocalMemberCount(Number.parseInt(group.member_count || 0, 10));
  }, [group.isJoined, group.member_count]);

  const handleToggleJoin = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // If not logged in, redirect to login
    if (!isLoggedIn) {
      // Store the group ID to check after login
      localStorage.setItem("pendingJoinGroupId", group.groupid);

      successToast("Please login to join groups", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      navigate("/signin", { state: { from: `/groups/${group.groupid}` } });
      return;
    }

    setLocalProcessing(true);

    try {
      if (localJoinedStatus) {
        // Check if the user is already not a member (defensive check)
        if (!group.isJoined) {
          successToast("You're not a member of this group", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
          });
          setLocalJoinedStatus(false);
          return;
        }

        onLeave && onLeave(group.groupid);
        setLocalJoinedStatus(false);
        setLocalMemberCount((prevCount) =>
          Math.max(0, Number.parseInt(prevCount, 10) - 1)
        );
      } else {
        // Check if the user is already joined (defensive check)
        if (group.isJoined) {
          successToast("You're already a member of this group", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
          });
          setLocalJoinedStatus(true);
          return;
        }

        onJoin && onJoin(group.groupid);
        setLocalJoinedStatus(true);
        setLocalMemberCount((prevCount) => Number.parseInt(prevCount, 10) + 1);
      }
    } catch (error) {
      console.error("Error toggling group membership:", error);
      errorToast(
        error.response?.data?.message ||
          error.message ||
          "Failed to update group membership"
      );
      setLocalJoinedStatus(group.isJoined);
      setLocalMemberCount(Number.parseInt(group.member_count || 0, 10));
    } finally {
      setLocalProcessing(false);
    }
  };

  // Add this effect to check for pending join after login
  useEffect(() => {
    // This will run when isLoggedIn changes (user logs in)
    if (isLoggedIn) {
      const pendingGroupId = localStorage.getItem("pendingJoinGroupId");

      // If this is the group the user was trying to join before login
      if (pendingGroupId && pendingGroupId === String(group.groupid)) {
        // Clear the pending join
        localStorage.removeItem("pendingJoinGroupId");

        // Check if already joined
        if (group.isJoined) {
          successToast("You're already a member of this group", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
          });
        }
      }
    }
  }, [isLoggedIn, group.groupid, group.isJoined]);

  // Render button based on login status and joined status
  const renderButton = () => {
    // If not logged in, show login button
    if (!isLoggedIn) {
      return (
        <button
          onClick={handleToggleJoin}
          className={`${
            compact ? "text-xs px-2 py-1" : "px-3 py-1.5 text-sm"
          } rounded-full flex items-center font-afacad bg-[#7B189F] text-white hover:bg-purple-700`}
        >
          <UserPlus className={`${compact ? "h-3 w-3" : "h-4 w-4"} mr-1`} />
          <span>Login to Join</span>
        </button>
      );
    }

    // If logged in, show joined/join button
    return (
      <button
        onClick={handleToggleJoin}
        disabled={localProcessing || isProcessing}
        className={`${
          compact ? "text-xs px-2 py-1" : "px-3 py-1.5 text-sm"
        } rounded-full flex items-center font-afacad ${
          localJoinedStatus
            ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
            : "bg-[#7B189F] text-white hover:bg-purple-700"
        }`}
      >
        {localProcessing || isProcessing ? (
          <div
            className={`${
              compact ? "h-3 w-3" : "h-4 w-4"
            } mr-1 border-2 rounded-full animate-spin ${
              localJoinedStatus
                ? "border-gray-300 border-t-gray-600"
                : "border-purple-300 border-t-white"
            }`}
          ></div>
        ) : localJoinedStatus ? (
          <UserCheck className={`${compact ? "h-3 w-3" : "h-4 w-4"} mr-1`} />
        ) : (
          <UserPlus className={`${compact ? "h-3 w-3" : "h-4 w-4"} mr-1`} />
        )}
        <span>{localJoinedStatus ? "Joined" : "Join"}</span>
      </button>
    );
  };

  if (compact) {
    return (
      <Link to={`/groups/${group.groupid}`} className="block">
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200">
          <div className="relative aspect-[16/9] w-full">
            <img
              src={group.coverphoto || "/placeholder.svg"}
              alt={group.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-2 left-2 right-2">
              <h3 className="text-white font-medium text-sm line-clamp-1 font-fraunces">
                {group.name}
              </h3>
            </div>
          </div>
          <div className="p-3">
            <p className="text-xs text-gray-500 line-clamp-2 min-h-[2rem] font-afacad">
              {group.description}
            </p>
            <div className="flex justify-between items-center mt-2">
              <div className="flex items-center text-xs text-gray-600 font-afacad">
                <Users className="h-3 w-3 mr-1" />
                <span>{localMemberCount} members</span>
              </div>
              {renderButton()}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/groups/${group.groupid}`} className="block">
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200">
        <div className="relative aspect-[16/9] w-full">
          <img
            src={group.coverphoto || "/placeholder.svg"}
            alt={group.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
          <div className="absolute bottom-3 left-3 right-3">
            <h3 className="text-white font-bold text-lg line-clamp-1 font-fraunces">
              {group.name}
            </h3>
          </div>
        </div>
        <div className="p-4">
          <p className="text-sm text-gray-600 line-clamp-2 min-h-[2.5rem] mb-3 font-afacad">
            {group.description}
          </p>
          <div className="flex justify-between items-center">
            <div className="flex items-center text-sm text-gray-600 font-afacad">
              <Users className="h-4 w-4 mr-1" />
              <span>
                {localMemberCount}{" "}
                {localMemberCount === 1 ? "member" : "members"}
              </span>
            </div>
            {renderButton()}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default GroupCard;
